import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { GroovePullquoteDirective } from '@groove/angular';
import {
  DataConfigAnchorDirective,
  DataConfigSpanDirective,
} from '../../../content/directives/personal-data.directive';
import { DisclosureGroupComponent } from '../../../content/disclosure/disclosure-group.component';
import { DisclosureComponent } from '../../../content/disclosure/disclosure.component';
import {
  JumpNavComponent,
  type JumpNavLink,
} from '../../../content/jump-nav/jump-nav.component';
import { PageCtaComponent } from '../../../content/page-cta/page-cta.component';
import { PricingBlockComponent } from '../../../content/pricing-block/pricing-block.component';
import {
  ScheduleStripComponent,
  type ScheduleStripItem,
} from '../../../content/schedule-strip/schedule-strip.component';
import { SectionHeadComponent } from '../../../content/section-head/section-head.component';
import { YoutubeSectionComponent } from '../../../content/youtube-section/youtube-section.component';
import { DATA_CONFIG } from '../../../injection-tokens';
import { HOME_TESTIMONIALS } from '../../bass-lessons/testimonials/testimonials-content/testimonials-data';

/**
 * The ten-section body of the home page — home IS the online-lessons hub
 * (spec docs/superpowers/specs/2026-09-07-online-lessons-hub-and-ia-design.md
 * §4, composition amended to ten beats by §13.1; decided 2026-09-06/07).
 * Built as the `/labs/home-a` prototype
 * (docs/superpowers/plans/2026-09-07-labs-prototypes.md) and promoted here
 * 2026-09-10 as the live `/` route.
 *
 * Follows the plan's locked "Hub story order" (word budgets are targets, not
 * gates), with round three's two additions in place: What this is → Who it's
 * for → Who's teaching → What you'd learn → How a lesson works → Why a live
 * teacher → Price and scheduling → What students say → FAQ → Getting set up.
 * The projection slot between the FAQ section and section 10 ("Bottom CTA")
 * is not one of the ten story-order sections — `HomeContentComponent`
 * (`src/app/routes/home/home-content/`, the `/` route) is the only component
 * that projects into it today, with its Russ-the-player band.
 *
 * Design pass (2026-09-07 evening, plan doc "Design pass" section):
 * recomposed from the inner-page idiom (a section heading, prose, a divider,
 * repeat) into the real home's editorial language — kicker + display heading
 * (`app-section-head`), open-paper editorial splits, and two recessed bands
 * (beats 1 and 5) instead of dividers.
 *
 * Refinement pass (2026-09-07 night, same plan doc, "Refinement pass"
 * section) — findings #1-#9, #13 land here:
 * - #1/#2: beat 4's copy rewritten in Russ's voice; the pulled-out "trade"
 *   callout is gone, its point folded into the paragraph as one line.
 * - #3: beat 2 drops its two-column split for one column; the photo is a
 *   performance shot, placed after the section-head, the pull-quote stays
 *   after the bio.
 * - #4: the two lesson photos render as a captioned pair (`<figure>`),
 *   beside "how a lesson runs".
 * - #5: the pricing block is card-only now (its `table` layout retired);
 *   the per-card call/email actions are gone (the mid-page CTA is the
 *   action) and the points list became one plain-language note per tier.
 * - #6: the mid-page CTA is capped at the beat's own text column
 *   (`.hub-mid-cta`), same as every other beat.
 * - #7: the FAQ is always open — the closed/open toggle is retired.
 * - #8: the "Already signed up? Zoom setup guide" line is gone — redundant
 *   with `<app-for-students>` immediately below on the host page. (Round
 *   five below removes that panel entirely; the jump-nav's own "Zoom
 *   setup" entry is the redundancy check now.)
 * - #13: (already restored by an earlier pass) the jump-nav's "Jump to:"
 *   label is `JumpNavComponent`'s own default — nothing to override here.
 *
 * Round two (2026-09-07, plan doc "Round two" section) — findings #3 and #7
 * land here:
 * - #3: beat 1's paragraph is rewritten so it doesn't repeat the hero's own
 *   one-line "what this is" + lesson promise, and a plain "Who it's for"
 *   subhead (`.hub-band-subhead`, no kicker) with the home page's own
 *   who-it's-for paragraphs verbatim now follows it, so both stay at the
 *   top of the page. No new jump-nav entry — the section id and the eight
 *   links are unchanged.
 * - #7: beat 6 restores the demo video (`app-youtube-section`) that fell
 *   out of the design pass's beat table — the real home's students beat,
 *   verbatim, between the section-head and the quotes grid.
 *
 * Round three (2026-09-11, Jeremy's review of the migrated site, spec §13.1)
 * — every item lands here or in the pricing block:
 * - #1: "Who it's for" is its own beat (second); the jump-nav drops "What
 *   this is" and adds the two new beats — nine entries.
 * - #2: "How a lesson works" is its own beat (fifth): the format paragraph
 *   and the lesson photo pair move out of "What you'd learn", plus a "What
 *   you need" half whose setup prices are read from the Zoom setup page.
 * - #3: first person everywhere Russ addresses the reader — the bio card and
 *   the projected player band are the only third-person frames — and the
 *   home route's FAQPage JSON-LD matches the visible answers word for word.
 * - #4: the foundations terms read as small headings (see the scss).
 * - #5: "Why a live teacher" carries the insider credential (130+ videos,
 *   linked to the What I Teach playlist; the big subscription sites,
 *   unnamed at the time). Named as examples 2026-09-14 (spec §13.1 item 5,
 *   amended): Scott's Bass Lessons, TalkingBass, TrueFire.
 * - #6/#7: payment methods as chips; the pricing block's policies link is
 *   dropped here (`policiesHref` is optional now).
 *
 * Round five (2026-09-14, Russ's review with Jeremy, spec §15.1-15.3):
 * - The jump-nav's "Zoom setup" entry leaves the page for
 *   `/bass-lessons/zoom-setup` instead of scrolling to the bottom reprise
 *   (§15.2) — the one `path`-shaped entry in this list, rendered by
 *   `JumpNavComponent` with a trailing arrow.
 * - "What you'd learn" gains one line after the foundations grid: "See
 *   everything I teach →" to `/bass-lessons/what-i-teach`.
 * - Payment methods drop to exactly three: Venmo, Zelle, PayPal.
 * - The shell's `<app-for-students>` panel (referenced in #8 above) is
 *   removed entirely — the footer's site map carries What I Teach and Zoom
 *   Setup instead (§15.2).
 */
@Component({
  selector: 'app-hub-body',
  imports: [
    RouterLink,
    GroovePullquoteDirective,
    DataConfigAnchorDirective,
    DataConfigSpanDirective,
    DisclosureComponent,
    DisclosureGroupComponent,
    JumpNavComponent,
    PageCtaComponent,
    PricingBlockComponent,
    ScheduleStripComponent,
    SectionHeadComponent,
    YoutubeSectionComponent,
  ],
  templateUrl: './hub-body.component.html',
  styleUrl: './hub-body.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HubBodyComponent {
  private readonly dataConfig = inject(DATA_CONFIG);

  // The same YouTube playlist What I Teach links ("Watch all 130+ lessons"),
  // for beat 6's "more than 130 of them myself" (round three #5).
  protected readonly playlistUrl = this.dataConfig.youtubeLessonsPlaylist;

  // One entry per hub-story-order section EXCEPT "What this is" — the nav
  // sits inside that beat, so a link to it is a link to where you already
  // are (round three #1) — plus "Zoom setup" for the closing CTA band
  // (plan: "Jump-nav entries: one per section, including 'Getting set up'
  // for the Zoom line"). Nine entries. Renamed to "Zoom setup" in round
  // five (spec §15.2) so the destination is explicit.
  protected readonly jumpNavLinks: readonly JumpNavLink[] = [
    { label: "Who it's for", fragment: 'who-its-for' },
    { label: "Who's teaching", fragment: 'who-is-teaching' },
    { label: "What you'd learn", fragment: 'what-you-learn' },
    { label: 'How a lesson works', fragment: 'how-a-lesson-works' },
    { label: 'Why a live teacher', fragment: 'why-live-teacher' },
    { label: 'Price and scheduling', fragment: 'price-and-scheduling' },
    { label: 'What students say', fragment: 'student-quotes' },
    { label: 'FAQ', fragment: 'faq' },
    // Round five (spec §15.2): leaves the page now — Russ expects the Zoom
    // setup guide here, not the bottom-of-page reprise. That reprise keeps
    // its id="getting-set-up" (a valid hubAnchor) but is no longer a jump
    // target.
    { label: 'Zoom setup', path: '/bass-lessons/zoom-setup' },
  ];

  // Round five (spec §15.3): exactly three, in this order — cash, check and
  // Cash App are gone.
  protected readonly pricingPaymentMethods: readonly string[] = [
    'Venmo',
    'Zelle',
    'PayPal',
  ];

  // Refinement pass finding #5: the card layout's old bullet-point lists
  // (hourlyPoints/prepaidPoints) became one plain-language note per tier, in
  // Russ's own words, given verbatim by the 2026-09-07 night refinement copy.
  protected readonly hourlyNote =
    'Best if you want a one-time lesson on something specific, or you take a lesson now and then when you need one.';
  protected readonly prepaidNote =
    'Best if you are taking lessons regularly. Buy four or more hours up front and every hour is $50.';

  // The three scheduling cadences as a term + one-liner three-up
  // (app-schedule-strip), replacing the old bulleted list.
  protected readonly scheduleItems: readonly ScheduleStripItem[] = [
    { term: 'Weekly', detail: 'One or two hour lesson every week.' },
    { term: 'Biweekly', detail: 'One or two hour lesson every 2 weeks.' },
    {
      term: 'Floater',
      // Round three #3: "contact Russ" → "get in touch" — Russ is speaking.
      detail: 'Schedule lessons as needed; get in touch when you are ready.',
    },
  ];

  // Five foundations, as a definition list (term = the link into
  // what-i-teach, detail = the one-liner). Not numbered — they are not a
  // sequence (plan: "Not numbered — they are not a sequence").
  protected readonly foundations: readonly {
    readonly term: string;
    readonly fragment: string;
    readonly detail: string;
  }[] = [
    {
      term: 'Technique & application',
      fragment: 'technique-and-application',
      // [draft]
      detail:
        'Right- and left-hand technique, bass line construction, soloing, and groove and time-feel.',
    },
    {
      term: 'Note mapping',
      fragment: 'note-mapping',
      // [draft]
      detail:
        'Arpeggios and scales, organized by what each note does over the chord you’re playing.',
    },
    {
      term: 'Styles',
      fragment: 'styles',
      // verbatim style list from what-i-teach-content.component.html
      detail:
        'Rock, pop, R&B, funk, blues, jazz, gospel, country, reggae, and Latin.',
    },
    {
      term: 'Ear development',
      fragment: 'ear-development',
      // [draft]
      detail:
        'Built mostly on transcription, training your ear so you can play what you hear.',
    },
    {
      term: 'Reading',
      fragment: 'reading',
      // [draft]
      detail:
        'Note names on the neck, rhythmic notation, and real professional bass charts.',
    },
  ];

  // The same trio, same order, as the real home page's "What students say"
  // (../../bass-lessons/testimonials/testimonials-content) — pulled by
  // stable key, rendered verbatim.
  protected readonly homeTestimonials = HOME_TESTIMONIALS;
}
